import { useCallback, useMemo } from "react";
import { print } from "graphql";
import type { TypedDocumentNode } from "@apollo/client";
import { useAuth } from "#app/lib/auth-context";
import {
  AUTH_API_BASE as LOGIN_URL,
  CORE_API_BASE as CORE_API,
  FEATURE_FLAGS_GRAPHQL_URL as FLAGS_API_URL,
} from "#app/lib/api-bases";

// ---------------------------------------------------------------------------
// Endpoints
//
// The admin console is a standalone, superuser-only, cross-workspace surface.
// It deliberately bypasses Apollo (which injects per-workspace headers) and
// talks to each service over plain fetch — same pattern the page has always
// used for the flags + auth APIs. Core admin queries are superuser-gated and
// carry no workspaceid header.
//
// Operations are NOT inline strings: they live in graphql/admin.graphql (core)
// and graphql/admin-flags/ (flags service) and are codegen'd into typed
// Documents. The helpers below `print()` those Documents over the fetch
// transport, so the queries stay schema-checked and typed without routing
// admin traffic through the workspace-scoped Apollo client.
// ---------------------------------------------------------------------------

const CORE_GQL = `${CORE_API.replace(/\/$/, "")}/graphql`;

// Service health is probed server-side by the core `systemHealth` query (see
// use-admin-health.ts), so no health URL is fetched from here.

// ---------------------------------------------------------------------------
// Fetch helpers (bound to the current access token)
// ---------------------------------------------------------------------------

type GqlResult<T> = { data?: T; errors?: { message: string }[] };

// Documents are static codegen constants, so their printed query string never
// changes — print() each one once and cache it rather than re-walking the AST
// on every request.
const printedQueries = new WeakMap<object, string>();
function printOnce(document: object): string {
  let query = printedQueries.get(document);
  if (query === undefined) {
    query = print(document as Parameters<typeof print>[0]);
    printedQueries.set(document, query);
  }
  return query;
}

export function useAdminFetch() {
  const { getAccessToken } = useAuth();

  const gql = useCallback(
    async <TData, TVars>(
      endpoint: string,
      document: TypedDocumentNode<TData, TVars>,
      variables: TVars,
    ): Promise<TData> => {
      const token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ query: printOnce(document), variables }),
      });
      const body = (await res.json()) as GqlResult<TData>;
      const firstError = body.errors?.[0];
      if (firstError) throw new Error(firstError.message);
      if (!body.data) throw new Error("Empty response");
      return body.data;
    },
    [getAccessToken],
  );

  const loginFetch = useCallback(
    async (path: string, init?: RequestInit): Promise<Response> => {
      const token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");
      return fetch(`${LOGIN_URL}${path}`, {
        ...init,
        headers: {
          ...(init?.headers ?? {}),
          Authorization: `Bearer ${token}`,
        },
      });
    },
    [getAccessToken],
  );

  return useMemo(
    () => ({
      flagsGql: <TData, TVars>(
        document: TypedDocumentNode<TData, TVars>,
        variables: TVars,
      ) => gql(FLAGS_API_URL, document, variables),
      coreGql: <TData, TVars>(
        document: TypedDocumentNode<TData, TVars>,
        variables: TVars,
      ) => gql(CORE_GQL, document, variables),
      loginFetch,
    }),
    [gql, loginFetch],
  );
}
